/**
 * @fileoverview
 * API Endpoints Constants
 * 
 * @version 1.0.0
 * @author KATALYST TEAM
 */

export const API_ENDPOINTS = {
  // Authentication
  AUTH: {
    LOGIN: '/auth/login',
    LOGOUT: '/auth/logout',
    REFRESH: '/auth/refresh',
    FORGOT_PASSWORD: '/auth/forgot-password',
    RESET_PASSWORD: '/auth/reset-password',
    ME: '/auth/me',
    PERMISSIONS: '/auth/permissions',
    CSRF_TOKEN: '/auth/csrf-token',
  },
  
  // OTP (global request/verify)
  OTP: {
    REQUEST: '/otp/request',
    VERIFY: '/otp/verify',
    VERIFY_BY_PASSWORD: '/otp/verify-by-password',
  },

  // Users
  USERS: {
    BASE: '/users',
    BY_ID: (id: number | string) => `/users/${id}`,
    STATISTICS: '/users/statistics',
    ACTIVATE: (id: number | string) => `/users/${id}/activate`,
    DEACTIVATE: (id: number | string) => `/users/${id}/deactivate`,
    CHANGE_PASSWORD: (id: number | string) => `/users/${id}/password`,
  },
  
  // Roles
  ROLES: {
    BASE: '/roles',
    DROPDOWN: '/roles/dropdown',
    BY_ID: (id: number | string) => `/roles/${id}`,
    STATISTICS: '/roles/statistics',
    PERMISSIONS: (id: number | string) => `/roles/${id}/permissions`,
    SYNC_PERMISSIONS: (id: number | string) => `/roles/${id}/permissions/sync`,
  },
  
  // Permissions
  PERMISSIONS: {
    BASE: '/permissions',
    ALL: '/permissions/all',
    BY_ID: (id: number | string) => `/permissions/${id}`,
    STATISTICS: '/permissions/statistics',
    BULK: '/permissions/bulk',
    MODULES: '/permissions/modules',
  },
  
  // Departments
  DEPARTMENTS: {
    BASE: '/departments',
    DROPDOWN: '/departments/dropdown',
    BY_ID: (id: number | string) => `/departments/${id}`,
    STATISTICS: '/departments/statistics',
  },
  
  // Regions
  REGIONS: {
    BASE: '/regions',
    DROPDOWN: '/regions/dropdown',
    BY_UUID: (uuid: string) => `/regions/${uuid}`,
  },
  
  // Branches
  BRANCHES: {
    BASE: '/branch',
    DROPDOWN: '/branch/dropdown',
    BY_UUID: (uuid: string) => `/branch/${uuid}`,
    BY_REGION: (regionUuid: string) => `/branch/region/${regionUuid}`,
  },
  
  // Generics
  GENERICS: {
    BASE: '/generics',
    DROPDOWN: '/generics/dropdown',
    BY_ID: (id: string) => `/generics/${id}`, // Now accepts UUID (string)
    VALUES_BY_SLUG: (slug: string) => `/generics/${slug}/values`,
    STATISTICS: '/generics/statistics',
  },
  
  // Audit Types
  AUDIT_TYPE: {
    BASE: '/audit-types',
    DROPDOWN: '/audit-types/dropdown',
    BY_CODE: (code: string) => `/audit-types/${code}`,
    BY_ID: (id: number | string) => `/audit-types/${id}`,
    PARTICULARS: (id: number | string) => `/audit-types/${id}/particulars`,
    PARTICULAR_BY_ID: (auditTypeId: number | string, particularId: number | string) => `/audit-types/${auditTypeId}/particulars/${particularId}`,
    STATISTICS: '/audit-types/statistics',
  },
  
  // Audit Pattern Templates
  AUDIT_PATTERN_TEMPLATES: {
    BASE: '/audit-pattern-templates',
    DROPDOWN: '/audit-pattern-templates/dropdown',
    BY_ID: (id: number | string) => `/audit-pattern-templates/${id}`,
  },
  
  // Particular Categories
  PARTICULAR_CATEGORIES: {
    BASE: '/particular-categories',
    DROPDOWN: '/particular-categories/dropdown',
    BY_ID: (id: number | string) => `/particular-categories/${id}`,
  },
  
  // Audits
  AUDITS: {
    // Global audit endpoints
    BASE: '/audit',
    CREATE: '/audit/create',
    START: (uuid: string) => `/audit/${uuid}/start`,
    RESUME: (uuid: string) => `/audit/${uuid}/resume`,
    COMPLETE: (uuid: string) => `/audit/${uuid}/complete`,
    FETCH_ACKNOWLEDGEMENT: (uuid: string) => `/audit/${uuid}/acknowledgement`,
    STEP: (uuid: string) => `/audit/${uuid}/step`,
    FETCH_IMAGE_URL: (uuid: string) => `/audit/images/${uuid}`,
    LIST: '/audit/list',
    // Branch audit
    BRANCH_AUDIT_FORM: (uuid: string) => `/branch-audit/${uuid}/form`,
    // Cash audit specific endpoints
    CASH: '/cash-audits',
    CASH_BY_ID: (id: string) => `/cash-audits/${id}`,
    CASH_DRAWER_IMAGE: (id: string) => `/cash-audits/${id}/drawer-image`,
    CASH_DELETE_DRAWER_IMAGE: (id: string, imageUuid: string) => `/cash-audits/${id}/drawer-image/${imageUuid}`,
    CASH_REGISTER_IMAGE: (id: string) => `/cash-audits/${id}/register-image`,
    CASH_DELETE_REGISTER_IMAGE: (id: string, imageUuid: string) => `/cash-audits/${id}/register-image/${imageUuid}`,
    CASH_DENOMINATIONS_MASTER: '/cash-audits/denominations/master',
    CASH_DENOMINATIONS: (id: string) => `/cash-audits/${id}/denominations`,
    CASH_REGISTER_CASH: (id: string) => `/cash-audits/${id}/register-cash`,
    CASH_SYSTEM_CASH: (id: string) => `/cash-audits/${id}/system-cash`,
    CASH_VARIANCE: (id: string) => `/cash-audits/${id}/variance/compute`,
    CASH_VARIANCE_DATA: (id: string) => `/cash-audits/${id}/variance-data`,
    CASH_GET_DRAWER_IMAGES: (id: string) => `/cash-audits/${id}/drawer-image`,
    CASH_GET_REGISTER_IMAGES: (id: string) => `/cash-audits/${id}/register-image`,
    CASH_ACKNOWLEDGEMENT: (id: string) => `/cash-audits/${id}/ack`,
    // CASH_ACKNOWLEDGEMENT_DATA: (id: string) => `/cash-audits/${id}/acknowledgement`,
    CASH_OTP_REQUEST: (id: string) => `/cash-audits/${id}/ack/otp/request`,
    CASH_OTP_VERIFY: (id: string) => `/cash-audits/${id}/ack/otp/verify`,
    CASH_CANCEL: (id: string) => `/cash-audits/${id}/cancel`,
    CASH_COMPLETE: (id: string) => `/cash-audits/${id}/complete`,
    // Register & Statutory Audits
    REGISTER_STATUTORY: '/audits/register-statutory',
    REGISTER_STATUTORY_BY_UUID: (uuid: string) => `/audits/register-statutory/${uuid}`,
    REGISTER_STATUTORY_STATUTORY: (uuid: string) => `/audits/register-statutory/${uuid}/statutory`,
    REGISTER_STATUTORY_STATUTORY_BATCH: (uuid: string) => `/audits/register-statutory/${uuid}/statutory`,
    REGISTER_STATUTORY_REGISTERS: (uuid: string) => `/audits/register-statutory/${uuid}/registers`,
    REGISTER_STATUTORY_REGISTERS_BATCH: (uuid: string) => `/audits/register-statutory/${uuid}/registers`,
    REGISTER_STATUTORY_SUMMARY: (uuid: string) => `/audits/register-statutory/${uuid}/summary`,
    REGISTER_STATUTORY_IMAGES: (uuid: string) => `/audits/register-statutory/${uuid}/images`,
    REGISTER_STATUTORY_REQUEST_OTP: (uuid: string) => `/audits/register-statutory/${uuid}/acknowledgement/request-otp`,
    REGISTER_STATUTORY_VERIFY_OTP: (uuid: string) => `/audits/register-statutory/${uuid}/acknowledgement/verify-otp`,
    REGISTER_STATUTORY_COMPLETE: (uuid: string) => `/audits/register-statutory/${uuid}/complete`,
    // Security Audits
    SECURITY_AUDIT: '/audits/security-audit',
    SECURITY_AUDIT_SECURITY_STATUSES: (uuid: string) => `/audits/security-audit/${uuid}/security-statuses`,
    SECURITY_AUDIT_VARIANCE: (uuid: string) => `/audits/security-audit/${uuid}/variance`,
    SECURITY_AUDIT_REQUEST_OTP: (uuid: string) => `/audits/security-audit/${uuid}/acknowledgement/request-otp`,
    SECURITY_AUDIT_VERIFY_OTP: (uuid: string) => `/audits/security-audit/${uuid}/acknowledgement/verify-otp`,
    // Asset Audits (slug: asset-audit for API path; list uses module=ASSET)
    ASSET_AUDIT: '/audits/asset-audit',
    ASSET_AUDIT_ASSET_ENTRIES: (uuid: string) => `/audits/asset-audit/${uuid}/asset-entries`,
    ASSET_AUDIT_ASSET_EVALUATIONS: (uuid: string) => `/audits/asset-audit/${uuid}/asset-evaluations`,
    ASSET_AUDIT_VARIANCE: (uuid: string) => `/audits/asset-audit/${uuid}/variance`,
    // Key Audits (path: audits/key-audit; list/create use module=KEY)
    KEY_AUDIT: '/audits/key-audit',
    KEY_AUDIT_VAULT_KEYS: (branchId: number | string) => `/audits/key-audit/branch/${branchId}/vault-keys`,
    KEY_AUDIT_EVALUATIONS: (uuid: string) => `/audits/key-audit/${uuid}/evaluations`,
    KEY_AUDIT_VARIANCE: (uuid: string) => `/audits/key-audit/${uuid}/variance`,
    KEY_AUDIT_EXCESS: (uuid: string) => `/audits/key-audit/${uuid}/excess`,
    KEY_AUDIT_KEY_EVALUATIONS: (auditUuid: string) => `/audits/key-audit/${auditUuid}/key-evaluations`,
    GOLD: '/audits/gold',
    GOLD_VERIFICATION: '/audits/gold-verification',
    GOLD_VERIFICATION_BY_UUID: (uuid: string) => `/audits/gold-verification/${uuid}`,
    GOLD_VERIFICATION_LOANS: (uuid: string) => `/audits/gold-verification/${uuid}/loans`,
    GOLD_VERIFICATION_LOAN_BY_ID: (uuid: string, loanId: number | string) => `/audits/gold-verification/${uuid}/loans/${loanId}`,
    GOLD_VERIFICATION_LOSS_ENTRY: (uuid: string, loanId: number | string) => `/audits/gold-verification/${uuid}/loans/${loanId}/loss-entry`,
    GOLD_VERIFICATION_LOSS_ENTRY_BY_ID: (uuid: string, loanId: number | string, id: number | string) => `/audits/gold-verification/${uuid}/loans/${loanId}/loss-entry/${id}`,
    GOLD_VERIFICATION_LOSS_ENTRIES_BULK: (uuid: string, loanId: number | string) => `/audits/gold-verification/${uuid}/loans/${loanId}/loss-entries/bulk`,
    GOLD_VERIFICATION_VARIANCE_SUMMARY: (uuid: string, loanId: number | string) => `/audits/gold-verification/${uuid}/loans/${loanId}/variance-summary`,
    GOLD_VERIFICATION_EVR_CHECKS: (uuid: string, loanId: number | string) => `/audits/gold-verification/${uuid}/loans/${loanId}/evr-checks`,
    GOLD_VERIFICATION_START: (uuid: string) => `/audits/gold-verification/${uuid}/start`,
    GOLD_VERIFICATION_SKIP_LOAN: (uuid: string, loanId: number | string) => `/audits/gold-verification/${uuid}/loans/${loanId}/skip`,
    GOLD_VERIFICATION_FORCE_CLOSE: (uuid: string) => `/audits/gold-verification/${uuid}/force-close`,
    GOLD_VERIFICATION_ACKNOWLEDGEMENT: (uuid: string) => `/audits/gold-verification/${uuid}/acknowledgement`,
    GOLD_VERIFICATION_REQUEST_OTP: (uuid: string) => `/audits/gold-verification/${uuid}/acknowledgement/request-otp`,
    GOLD_VERIFICATION_VERIFY_OTP: (uuid: string) => `/audits/gold-verification/${uuid}/acknowledgement/verify-otp`,
    VALUATION_TOOL: '/audits/valuation-tool',
    VALUATION_TOOL_BY_UUID: (uuid: string) => `/audits/valuation-tool/${uuid}`,
    VALUATION_TOOL_TOOLS: (uuid: string) => `/audits/valuation-tool/${uuid}/tools`,
    VALUATION_TOOL_TOOLS_BATCH: (uuid: string) => `/audits/valuation-tool/${uuid}/tools`,
    VALUATION_TOOL_REQUEST_OTP: (uuid: string) => `/audits/valuation-tool/${uuid}/acknowledgement/request-otp`,
    VALUATION_TOOL_SAVE_EVALUATION: (uuid: string, particularId: number | string) => `/audits/valuation-tool/${uuid}/tools/${particularId}`,
    VALUATION_TOOL_UPLOAD_IMAGE: (uuid: string, particularId: number | string) => `/audits/valuation-tool/${uuid}/tools/${particularId}/images`,
    VALUATION_TOOL_SUMMARY: (uuid: string) => `/audits/valuation-tool/${uuid}/summary`,
    VALUATION_TOOL_VERIFY_OTP: (uuid: string) => `/audits/valuation-tool/${uuid}/acknowledgement/verify-otp`,
    PACKET_INVENTORY: '/audits/packet-inventory',
    PACKET_INVENTORY_BY_UUID: (uuid: string) => `/audits/packet-inventory/${uuid}`,
    PACKET_INVENTORY_PACKETS: (id: number | string) => `/audits/packet-inventory/${id}/packets`,
    PACKET_INVENTORY_PACKET_UPDATE: (uuid: string, packetId: number | string) => `/audits/packet-inventory/${uuid}/packets/${packetId}`,
    PACKET_INVENTORY_PACKETS_BULK: (uuid: string) => `/audits/packet-inventory/${uuid}/packets/bulk`,
    PACKET_INVENTORY_VARIANCE_SUMMARY: (id: number | string) => `/audits/packet-inventory/${id}/variance-summary`,
    PACKET_INVENTORY_EXCESS: (uuid: string) => `/audits/packet-inventory/${uuid}/excess`,
    PACKET_INVENTORY_ADDITIONAL_CHECKS: (uuid: string) => `/audits/packet-inventory/${uuid}/additional-checks`,
    PACKET_INVENTORY_STOCK_IRREGULARITIES: (uuid: string) => `/audits/packet-inventory/${uuid}/stock-irregularities`,
    PACKET_INVENTORY_MISMATCH_HANDLING: (uuid: string) => `/audits/packet-inventory/${uuid}/mismatch-handling`,
    PACKET_INVENTORY_GL_NUMBERS: (uuid: string) => `/audits/packet-inventory/${uuid}/gl-numbers`,
    PACKET_INVENTORY_REQUEST_OTP: (uuid: string) => `/audits/packet-inventory/${uuid}/acknowledgement/request-otp`,
    PACKET_INVENTORY_VERIFY_OTP: (uuid: string) => `/audits/packet-inventory/${uuid}/acknowledgement/verify-otp`,
    PACKET_INVENTORY_ACKNOWLEDGEMENT_DATA: (uuid: string) => `/audit/${uuid}/acknowledgement`,
    TARE_WEIGHT: '/audits/tare-weight',
    TARE_WEIGHT_PACKETS: (id: number | string) => `/audits/tare-weight/${id}/packets`,
    TARE_WEIGHT_PACKETS_BULK: (uuid: string) => `/audits/tare-weight/${uuid}/packets/bulk`,
    TARE_WEIGHT_PACKET_UPDATE: (uuid: string, packetId: number | string) => `/audits/tare-weight/${uuid}/packets/${packetId}`,
    TARE_WEIGHT_UPLOAD_IMAGE: (uuid: string, packetId: number | string) => `/audits/tare-weight/${uuid}/packets/${packetId}/images`,
    TARE_WEIGHT_VARIANCE_SUMMARY: (id: number | string) => `/audits/tare-weight/${id}/variance-summary`,
    TARE_WEIGHT_GL_NUMBERS: (uuid: string) => `/audits/tare-weight/${uuid}/gl-numbers`,
    TARE_WEIGHT_ADDITIONAL_CHECKS: (uuid: string) => `/audits/tare-weight/${uuid}/additional-checks`,
    TARE_WEIGHT_STOCK_IRREGULARITIES: (uuid: string) => `/audits/tare-weight/${uuid}/stock-irregularities`,
    TARE_WEIGHT_START_AUDIT: (uuid: string) => `/audits/tare-weight/${uuid}/start`,
    TARE_WEIGHT_REQUEST_OTP: (uuid: string) => `/audits/tare-weight/${uuid}/acknowledgement/request-otp`,
    TARE_WEIGHT_VERIFY_OTP_ONLY: (uuid: string) => `/audits/tare-weight/${uuid}/ack/otp/verify`,
    TARE_WEIGHT_COMPLETE: (uuid: string) => `/audits/tare-weight/${uuid}/complete`,
    // TARE_WEIGHT_ACKNOWLEDGEMENT_DATA: (uuid: string) => `/audits/tare-weight/${uuid}/acknowledgement`,
    SURVEILLANCE: '/audits/surveillance',
    ROUTINE_CHECKING: '/routine-checking',
    ROUTINE_CHECKING_BY_UUID: (uuid: string) => `/routine-checking/${uuid}`,
    ROUTINE_CHECKING_PAST_ACTIVITIES: (auditUuid: string) => `/routine-checking/${auditUuid}/past-activities`,
    ROUTINE_CHECKING_RAISE_QUERY: (id: string) => `/routine-checking/${id}/queries`,
    ROUTINE_CHECKING_UPLOAD_QUERY: (id: string) => `/routine-checking/${id}/queries/upload`,
    ROUTINE_CHECKING_IMAGE_URL: (imageId: number | string, expiresIn?: number) => {
      const baseUrl = `/routine-checking/images/${imageId}/url`;
      return expiresIn ? `${baseUrl}?expiresIn=${expiresIn}` : baseUrl;
    },
    ROUTINE_CHECKING_VIDEO_URL: (videoId: number | string, expiresIn?: number) => {
      const baseUrl = `/routine-checking/videos/${videoId}/url`;
      return expiresIn ? `${baseUrl}?expiresIn=${expiresIn}` : baseUrl;
    },
    LOAN_PROCESS_CHECKING: '/loan-process-checking',
    LOAN_PROCESS_CHECKING_DETAILS: (loanId: number | string) => `/loan-process-checking/loans/${loanId}/details`,
    LOAN_PROCESS_CHECKING_RAISE_QUERY: (auditId: string) => `/loan-process-checking/${auditId}/queries`,
    LOAN_PROCESS_CHECKING_RAISE_QUERY_UPLOAD: (auditId: string) =>
      `/loan-process-checking/${auditId}/queries/upload`,
    LOAN_PROCESS_CHECKING_LOANS: (uuid: string) => `/loan-process-checking/${uuid}/loans`,
    LOAN_PROCESS_CHECKING_QUERIES_BY_AUDIT_LOAN: (auditLoanId: number) =>
      `/loan-process-checking/loans/${auditLoanId}/queries`,
    LOAN_PROCESS_CHECKING_DOCUMENT_URL: (documentId: number | string, auditId: number | string, expiresIn?: number) => {
      const baseUrl = `/loan-process-checking/documents/${documentId}/url`;
      const params = new URLSearchParams();
      if (auditId) {
        params.append('auditId', String(auditId));
      }
      if (expiresIn) {
        params.append('expiresIn', String(expiresIn));
      }
      const queryString = params.toString();
      return queryString ? `${baseUrl}?${queryString}` : baseUrl;
    },
    LOAN_PROCESS_CHECKING_START: (id: string) => `/loan-process-checking/${id}/start`,
    LOAN_PROCESS_CHECKING_COMPLETE: (auditId: string) => `/loan-process-checking/${auditId}/complete`,
    LOAN_PROCESS_CHECKING_VIDEO_URL: (videoId: number | string) =>
      `/loan-process-checking/videos/${videoId}/url`,
    ONLINE: '/audits/online',
    // KYC & Disbursement
    KYC_DISBURSEMENT: '/kyc-disbursement',
    KYC_DISBURSEMENT_BY_UUID: (uuid: string) => `/kyc-disbursement/${uuid}`,
    KYC_DISBURSEMENT_START: (uuid: string) => `/kyc-disbursement/${uuid}/start`,
    KYC_DISBURSEMENT_ACTIVE_LOANS: '/kyc-disbursement/loans/active',
    KYC_DISBURSEMENT_ENSURE_AUDIT: (loanId: number | string) => `/kyc-disbursement/loans/${loanId}/ensure-audit`,
    KYC_DISBURSEMENT_LOANS_BY_BRANCH: (branchId: number | string) => `/kyc-disbursement/branches/${branchId}/loans`,
    KYC_DISBURSEMENT_LOAN_DETAILS: (loanId: number | string) => `/kyc-disbursement/loans/${loanId}/details`,
    KYC_DISBURSEMENT_LOAN_DOCUMENTS: (loanId: number | string) => `/kyc-disbursement/loans/${loanId}/documents`,
    KYC_DISBURSEMENT_DOCUMENT_URL: (documentId: number | string, expiresIn?: number) => {
      const baseUrl = `/kyc-disbursement/documents/${documentId}/url`;
      return expiresIn ? `${baseUrl}?expiresIn=${expiresIn}` : baseUrl;
    },
    KYC_DISBURSEMENT_RAISE_QUERY: (uuid: string) => `/kyc-disbursement/${uuid}/queries`,
    KYC_DISBURSEMENT_GET_QUERIES: (uuid: string) => `/kyc-disbursement/${uuid}/queries`,
    KYC_DISBURSEMENT_UPDATE_DOCUMENT_STATUS: (uuid: string, documentId: number | string) => `/kyc-disbursement/${uuid}/documents/${documentId}/status`,
    KYC_DISBURSEMENT_DOCUMENT_STATUSES: (uuid: string) => `/kyc-disbursement/${uuid}/documents/statuses`,
    KYC_DISBURSEMENT_CUSTOMER_LOANS: (customerId: number | string, branchId: number | string) => `/kyc-disbursement/customers/${customerId}/branches/${branchId}/loans`,
    KYC_DISBURSEMENT_SAVE_DRAFT: (uuid: string) => `/kyc-disbursement/${uuid}/save-draft`,
    KYC_DISBURSEMENT_COMPLETE: (uuid: string) => `/kyc-disbursement/${uuid}/complete`,
    KYC_DISBURSEMENT_COMPLETE_LOAN: (loanId: number | string) => `/kyc-disbursement/loans/${loanId}/complete`,
    // Release Audit
    RELEASE_AUDIT: '/release-audit',
    RELEASE_AUDIT_BY_UUID: (uuid: string) => `/release-audit/${uuid}`,
    RELEASE_AUDIT_START: (uuid: string) => `/release-audit/${uuid}/start`,
    RELEASE_AUDIT_ACTIVE_LOANS: '/release-audit/loans/active',
    RELEASE_AUDIT_ENSURE_AUDIT: (loanId: number | string) => `/release-audit/loans/${loanId}/ensure-audit`,
    RELEASE_AUDIT_COMPLETE_LOAN: (loanId: number | string) => `/release-audit/loans/${loanId}/complete`,
    RELEASE_AUDIT_LOANS_BY_BRANCH: (branchId: number | string) => `/release-audit/loans/${branchId}`,
    RELEASE_AUDIT_LOAN_DETAILS: (loanId: number | string) => `/release-audit/loans/${loanId}/details`,
    RELEASE_AUDIT_DOCUMENT_URL: (documentId: number | string, expiresIn?: number) => {
      const baseUrl = `/release-audit/documents/${documentId}/url`;
      return expiresIn ? `${baseUrl}?expiresIn=${expiresIn}` : baseUrl;
    },
    RELEASE_AUDIT_RAISE_QUERY: (uuid: string) => `/release-audit/${uuid}/queries`,
    RELEASE_AUDIT_GET_QUERIES: (uuid: string) => `/release-audit/${uuid}/queries`,
    RELEASE_AUDIT_UPDATE_QUERY: (queryId: number | string) => `/release-audit/queries/${queryId}`,
    RELEASE_AUDIT_DELETE_QUERY: (queryId: number | string) => `/release-audit/queries/${queryId}/delete`,
    RELEASE_AUDIT_SAVE_DRAFT: (uuid: string) => `/release-audit/${uuid}/save-draft`,
    RELEASE_AUDIT_COMPLETE: (uuid: string) => `/release-audit/${uuid}/complete`,
    LMS_DEAL_MOVEMENT: (loanUuid: string) => `/lms/deal/movement/${loanUuid}`,
  },
  
  // Query Management
  QUERY_MANAGEMENT: {
    BASE: '/query-management',
    LIST: '/query-management',
    DETAILS: (uuid: string) => `/query-management/${uuid}/details`,
    CONVERSATIONS: (uuid: string) => `/query-management/${uuid}/conversations`,
    SEND_BACK: (uuid: string) => `/query-management/${uuid}/send-back`,
    MARK_RESOLVED: (uuid: string) => `/query-management/${uuid}/mark-resolved`,
    SAVE_DRAFT: (uuid: string) => `/query-management/${uuid}/save-draft`,
    SUBMIT_RESPONSE: (uuid: string) => `/query-management/${uuid}/submit-response`,
  },
  
  // Reports
  REPORTS: {
    BASE: '/reports',
    GENERATE: '/reports/generate',
    EXPORT: '/reports/export',
  },
  
  // Audit Schedules
  AUDIT_SCHEDULES: {
    BASE: '/audit-schedules',
    GENERATE_PREVIEW: '/audit-schedules/generate-preview',
    REGENERATE_PREVIEW: (uuid: string) => `/audit-schedules/${uuid}/generate-preview`,
    BY_UUID: (uuid: string) => `/audit-schedules/${uuid}`,
    PREVIEW: (uuid: string) => `/audit-schedules/${uuid}/preview`,
    PUBLISH: (uuid: string) => `/audit-schedules/${uuid}/publish`,
    CANCEL: (uuid: string) => `/audit-schedules/${uuid}/cancel`,
  },
  
  // Dashboard
  DASHBOARD: {
    STATS: '/dashboard/stats',
    AUDIT_STATUS_OVERVIEW: '/dashboard/audit-status-overview',
    AUDIT_STATUS_AND_TYPES: '/dashboard/audit-status-and-types',
    DATA: '/dashboard',
    PERFORMANCE_METRICS: '/dashboard/performance-metrics',
    RECENT_AUDITS: '/dashboard/recent-audits',
    RECENT_ACTIVITIES: '/dashboard/recent-activities',
    AUDIT_TRENDS: '/dashboard/audit-trends',
    TOP_AUDITORS: '/dashboard/top-auditors',
    SLA_BREACHES: '/dashboard/sla-breaches',
    HIGH_RISK_AUDITS: '/dashboard/high-risk-audits',
  },
  
  // Settings
  SETTINGS: {
    AUDIT_SCHEDULE: '/settings/audit-schedule',
    USER_MANAGEMENT: '/settings/user-management',
    DYNAMIC_FORM_CREATION: '/settings/dynamic-form-creation',
    DYNAMIC_FORM_FORM_NAMES: '/settings/dynamic-form-creation/form-names',
    DATABASE_INTEGRATION: '/settings/database-integration',
  },
  
  // Entry Masters (asset entry – list, create, get by uuid, resume)
  ENTRY_MASTERS: {
    LIST: '/entry-masters',
    CREATE: '/entry-masters',
    BY_UUID: (uuid: string) => `/entry-masters/${uuid}`,
    RESUME: (uuid: string) => `/entry-masters/${uuid}/resume`,
    START: (uuid: string) => `/entry-masters/${uuid}/start`,
    STEP_UPDATE: (uuid: string) => `/entry-masters/${uuid}/step-update`,
    COMPLETE: (uuid: string) => `/entry-masters/${uuid}/complete`,
    GET_ACKNOWLEDGEMENT: (uuid: string) => `/entry-masters/${uuid}/acknowledgement`,
  },

  // Asset Entries (GET list + POST create on same path, GET by entry master, GET/PUT/DELETE by uuid)
  ASSET_ENTRIES: {
    LIST: '/asset-entries',
    CREATE: '/asset-entries/create',
    BY_ENTRY_MASTER: (entryMasterId: number) => `/asset-entries/entry-master/${entryMasterId}`,
    BY_UUID: (uuid: string) => `/asset-entries/${uuid}`,
  },

  // Vault Key Entries (create vault key under an entry master; list by entry master)
  VAULT_KEY_ENTRIES: {
    CREATE: '/vault-key-entries/create',
    BY_ENTRY_MASTER: (entryMasterId: number) => `/vault-key-entries/entry-master/${entryMasterId}`,
    BY_UUID: (uuid: string) => `/vault-key-entries/${uuid}`,
    /** GET – all vault key entries for dropdowns (branch + vault labels) */
    DROPDOWN: '/vault-key-entries/dropdown',
  },

  // Key Masters (keys under a vault key entry)
  KEY_MASTERS: {
    CREATE: '/key-masters/create',
    BY_VAULT_KEY: (vaultKeyId: number) => `/key-masters/vault-key/${vaultKeyId}`,
    BY_UUID: (uuid: string) => `/key-masters/${uuid}`,
  },

  // Key Movement Sender – vault keys by branch, save, variance, branch movement
  KEY_MOVEMENT_SENDER: {
    VAULT_KEYS_BY_BRANCH: (branchId: number) => `/key-movement-sender/branch/${branchId}/vault-keys`,
    SAVE: '/key-movement-sender/save',
    VARIANCE_BY_ENTRY_MASTER: (entryMasterId: number) => `/key-movement-sender/entry-master/${entryMasterId}/variance`,
    BRANCH_MOVEMENT: (entryMasterId: number) => `/key-movement-sender/entry-master/${entryMasterId}/branch-movement`,
    CARRIER_ACKNOWLEDGEMENT: (branchMovementId: number) =>
      `/key-movement-sender/branch-movement/${branchMovementId}/carrier-acknowledgement`,
    KEY_MOVEMENT_DATA: (entryMasterId: number) =>
      `/key-movement-sender/entry-master/${entryMasterId}/key-movement-data`,
  },

  // Key Movement Receiver – get data by receiver entry master UUID
  KEY_MOVEMENT_RECEIVER: {
    BY_UUID: (uuid: string) => `/key-movement-receiver/${uuid}`,
  },

  // Scorecards (GET list, GET by uuid, POST create)
  SCORECARDS: {
    BASE: '/scorecards',
    BY_UUID: (uuid: string) => `/scorecards/${uuid}`,
  },

  // Score Card Scoring Models (GET list, GET by uuid, POST create, PATCH update, DELETE, DELETE parameter link)
  SCORING_MODELS: {
    BASE: '/scoring-models',
    BY_UUID: (uuid: string) => `/scoring-models/${uuid}`,
    DELETE: (uuid: string) => `/scoring-models/${uuid}`,
    DELETE_PARAMETER: (parameterUuid: string) => `/scoring-models/parameters/${parameterUuid}`,
  },

  // Score Card Parameters (GET list, POST create, dropdowns for tables/columns and active parameters)
  PARAMETERS: {
    BASE: '/parameters',
    UPDATE: (uuid: string) => `/parameters/${uuid}`,
    DELETE: (uuid: string) => `/parameters/${uuid}`,
    RULES: (uuid: string) => `/parameters/${uuid}/rules`,
    RULE_DELETE: (ruleUuid: string) => `/parameters/rules/${ruleUuid}`,
    /** GET active parameters for dropdown (search, limit). */
    DROPDOWN: '/parameters/dropdown',
    DROPDOWN_TABLES: '/parameters/dropdown/tables',
    DROPDOWN_COLUMNS: '/parameters/dropdown/columns',
  },

  // System configuration (Settings – ACKNOWLEDGEMENT, COLLATERAL_IMAGE_PATH)
  SYSTEM_CONFIG: {
    BASE: '/config',
    BY_MODULE: (module: string) => `/config/${encodeURIComponent(module)}`,
    PATCH_BY_ID: (id: number | string) => `/config/${id}`,
  },

  // Health
  HEALTH: '/health',
  
  // LMS Deal
  LMS_DEAL: {
    VIEWER: (loanUuid: string) => `/lms/deal/viewer/${loanUuid}`,
    MOVEMENT: (loanUuid: string) => `/lms/deal/movement/${loanUuid}`,
  },

  // LMS Loan
  LMS_LOAN: {
    /** GET /api/v1/lms/loan/check-loan-no?loanNo=...&auditId=...&branchId=... - used in Add Excess flow to validate GL/loan number */
    CHECK_LOAN_NO: (loanNo: string, auditId?: number, branchId?: number) => {
      const params = new URLSearchParams();
      params.append('loanNo', loanNo);
      if (auditId !== undefined && auditId !== null) {
        params.append('auditId', String(auditId));
      }
      if (branchId !== undefined && branchId !== null) {
        params.append('branchId', String(branchId));
      }
      return `/lms/loan/check-loan-no?${params.toString()}`;
    },
    LOAN_NUMBERS: (search?: string, limit?: number) => {
      const params = new URLSearchParams();
      if (search) params.append('search', search);
      if (limit !== undefined) params.append('limit', String(limit));
      const queryString = params.toString();
      return queryString ? `/lms/loan/loan-numbers?${queryString}` : '/lms/loan/loan-numbers';
    },
  },
} as const;

